// src/components/contact-form.tsx
"use client";

import { useState } from "react";
import { getFirebaseDb } from "@/lib/firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";

export async function sendContactMessage(payload: {
  firstName: string;
  lastName: string;
  email: string;
  interest: "hiring" | "collaboration" | "other";
  message: string;
}) {
  const db = getFirebaseDb();
  await addDoc(collection(db, "contact_messages"), {
    ...payload,
    email: payload.email.trim().toLowerCase(),
    status: "new",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    meta: {
      url: typeof window !== "undefined" ? window.location.pathname : "",
      referer: typeof document !== "undefined" ? document.referrer : "",
    },
  });
}

export default function ContactForm() {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<"idle" | "ok" | "err">("idle");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const firstName = String(fd.get("firstName") ?? "").trim();
    const lastName = String(fd.get("lastName") ?? "").trim();
    const email = String(fd.get("email") ?? "").trim();
    const interest = (String(fd.get("interest") ?? "hiring") as
      | "hiring"
      | "collaboration"
      | "other");
    const message = String(fd.get("message") ?? "").trim();

    if (!firstName || !email || !message) {
      setSent("err");
      return;
    }

    setSending(true);
    try {
      await sendContactMessage({ firstName, lastName, email, interest, message });
      setSent("ok");
      (e.currentTarget as HTMLFormElement).reset();
    } catch (err) {
      console.error(err);
      setSent("err");
    } finally {
      setSending(false);
    }
  }

  return (
    <section id="contact" className="px-8 py-16 scroll-mt-24">
      <div className="container mx-auto mb-20 text-center">
        <h1 className="mb-4 text-3xl lg:text-4xl font-bold text-blue-gray-900">Contact Me</h1>
        <p className="mx-auto w-full lg:w-5/12 text-gray-500">
          Ready to get started? Feel free to reach me out through the contact form or send me an email.
        </p>
      </div>

      <div className="container mx-auto border border-gray/200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-7">
          {/* Left panel */}
          <div className="col-span-3 bg-gray-900 p-8 md:p-16">
            <h2 className="mb-4 text-2xl font-semibold text-white">Contact Information</h2>

            <div className="flex items-center gap-3 text-white">
              <span className="inline-block h-6 w-6 rounded bg-white/10 grid place-items-center">📞</span>
              <span>+61 433 853 330</span>
            </div>
            <div className="mt-3 flex items-center gap-3 text-white">
              <span className="inline-block h-6 w-6 rounded bg-white/10 grid place-items-center">✉️</span>
              <span>contact@felipeyarns.au</span>
            </div>

            <div className="mt-6 flex items-center gap-5 text-white/90">
              <a href="#" aria-label="Facebook" className="hover:text-white">Facebook</a>
              <a href="#" aria-label="Instagram" className="hover:text-white">Instagram</a>
              <a href="#" aria-label="GitHub" className="hover:text-white">GitHub</a>
            </div>
          </div>

          {/* Right form */}
          <div className="col-span-4 p-6 md:p-10">
            <form onSubmit={onSubmit} className="space-y-6">
              <div className="grid gap-6 lg:grid-cols-2">
                <div className="flex flex-col">
                  <label htmlFor="firstName" className="text-sm text-gray-600 mb-1">First Name</label>
                  <input
                    id="firstName"
                    name="firstName"
                    required
                    autoComplete="given-name"
                    placeholder="eg. Amy"
                    className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
                <div className="flex flex-col">
                  <label htmlFor="lastName" className="text-sm text-gray-600 mb-1">Last Name</label>
                  <input
                    id="lastName"
                    name="lastName"
                    autoComplete="family-name"
                    placeholder="eg. Jones"
                    className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
              </div>

              <div className="flex flex-col">
                <label htmlFor="email" className="text-sm text-gray-600 mb-1">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="eg. amy@mail.com"
                  className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>

              <fieldset>
                <legend className="text-sm text-gray-600 mb-2">What are you interested in?</legend>
                <div className="flex flex-wrap gap-6">
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="interest" value="hiring" defaultChecked /> Hiring
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="interest" value="collaboration" /> Collaboration
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="interest" value="other" /> Other
                  </label>
                </div>
              </fieldset>

              <div className="flex flex-col">
                <label htmlFor="message" className="text-sm text-gray-600 mb-1">Your Message</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>

              <div className="flex items-center justify-end">
                <button
                  type="submit"
                  disabled={sending}
                  className={`px-5 py-2 rounded border text-sm ${
                    sending
                      ? "cursor-not-allowed border-gray-300 text-gray-400"
                      : "border-gray-500 text-gray-900 hover:bg-gray-900 hover:text-white"
                  }`}
                >
                  {sending ? "Sending..." : "Send message"}
                </button>
              </div>

              {sent === "ok" && (
                <p className="text-sm text-green-600">Message sent. I’ll get back to you soon.</p>
              )}
              {sent === "err" && (
                <p className="text-sm text-red-600">Please fill First name, Email and Message.</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}